import React from 'react';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Input from '../../../atoms/input.js';
import {HAC_STATUS} from '../../../../constants/constants.js';

const isReadOnlyStatus = (profileStatus) =>
  profileStatus === HAC_STATUS.Exited || profileStatus === HAC_STATUS.Nominated;

const shouldShowAddress = (profileStatus, address, showWhenNominated) =>
  profileStatus === HAC_STATUS.Active ||
  (showWhenNominated && profileStatus === HAC_STATUS.Nominated && address);

const HacAddressSection = ({
  t,
  countries,
  errors,
  register,
  privateAddress,
  companyAddress,
  preferredAddress,
  profileStatus,
  chooseAddressTextKey,
  showWhenNominated = false,
  disableWhenReadOnly = false,
  privateAliasPlaceholder,
  onPreferredAddressChange,
  onClearAddress,
  validatePrivateField,
  validateCompanyField,
}) => {
  const disabled = disableWhenReadOnly && isReadOnlyStatus(profileStatus);
  const showPreferredSelector = profileStatus === HAC_STATUS.Active;
  const showPrivateAddress = shouldShowAddress(
    profileStatus,
    privateAddress,
    showWhenNominated
  );
  const showCompanyAddress = shouldShowAddress(
    profileStatus,
    companyAddress,
    showWhenNominated
  );

  if (!showPrivateAddress && !showCompanyAddress) {
    return null;
  }

  return (
    <>
      {showPreferredSelector && (
        <>
          <p>{t(chooseAddressTextKey)}</p>
          <div className="form-control">
            <RadioGroup
              row
              aria-labelledby="preferred-address-radio-group"
              name="preferred-address-radio-group"
              value={preferredAddress}
              onChange={onPreferredAddressChange}
            >
              <FormControlLabel
                value="private"
                name="preferred_address"
                control={<Radio/>}
                label={t('common-private-address')}
              />
              <FormControlLabel
                value="company"
                name="preferred_address"
                control={<Radio/>}
                label={t('common-company-address')}
              />
            </RadioGroup>
          </div>
        </>
      )}

      {showPrivateAddress && (
        <div className="address">
          <div className="address-header">
            <h5>{t('common-private-address')}</h5>
            {privateAddress?.id && (
              <button
                type="button"
                className="btn-form btn text"
                onClick={() => onClearAddress('private')}
              >
                <span className="icon-delete"></span>
              </button>
            )}
          </div>

          <div className="form-control">
            <Input
              maxLength="50"
              type="text"
              id="alias_private"
              placeholder={privateAliasPlaceholder}
              disabled={disabled}
              label={t('common-alias')}
              error={errors?.private_address?.alias}
              {...register('private_address.alias', {
                validate: (value, allValues) =>
                  validatePrivateField('alias', allValues),
              })}
            />
          </div>

          <Grid container spacing={1}>
            <Grid size={{xs: 12, sm: 8, md: 8}}>
              <div className="form-control">
                <Input
                  maxLength="150"
                  type="text"
                  id="address_line_1_private"
                  disabled={disabled}
                  label={t('common-address-name')}
                  error={errors?.private_address?.address_line_1}
                  {...register('private_address.address_line_1', {
                    validate: (value, allValues) =>
                      validatePrivateField('address_line_1', allValues),
                  })}
                />
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="20"
                  type="text"
                  id="address_number_private"
                  disabled={disabled}
                  label={t('common-address-number')}
                  error={errors?.private_address?.address_number}
                  {...register('private_address.address_number', {
                    validate: (value, allValues) =>
                      validatePrivateField('address_number', allValues),
                  })}
                />
              </div>
            </Grid>
          </Grid>

          <div className="form-control">
            <label htmlFor="address_line_2_private">
              {t('common-address-line-2')}
            </label>
            <input
              maxLength="150"
              type="text"
              disabled={disabled}
              id="address_line_2_private"
              {...register('private_address.address_line_2')}
            />
          </div>

          <Grid container spacing={3}>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <label htmlFor="country_private">{t('common-country')}</label>
                <select
                  name="area_code_private"
                  id="country_private"
                  className="normal"
                  disabled={disabled}
                  error={errors?.private_address?.area_code}
                  {...register('private_address.area_code', {
                    validate: (value, allValues) =>
                      validatePrivateField('area_code', allValues),
                  })}
                >
                  <option key="" value="">
                    {t('common-select-country')}
                  </option>
                  {countries.map((country) => (
                    <option key={country.area_code} value={country.area_code}>
                      {t('countries-' + country.area_code)}
                    </option>
                  ))}
                </select>
                {errors?.private_address?.area_code && (
                  <span className="input__error">
                    {errors.private_address.area_code.message}
                  </span>
                )}
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="50"
                  type="text"
                  id="city_private"
                  disabled={disabled}
                  label={t('common-city')}
                  error={errors?.private_address?.city}
                  {...register('private_address.city', {
                    validate: (value, allValues) =>
                      validatePrivateField('city', allValues),
                  })}
                />
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="50"
                  type="text"
                  id="postal_code_private"
                  disabled={disabled}
                  label={t('common-zip-cp')}
                  error={errors?.private_address?.postal_code}
                  {...register('private_address.postal_code', {
                    validate: (value, allValues) =>
                      validatePrivateField('postal_code', allValues),
                  })}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      )}

      {showCompanyAddress && (
        <div className="address">
          <div className="address-header">
            <h5>{t('common-company-address')}</h5>
            {companyAddress?.id && (
              <button
                type="button"
                className="btn-form btn text"
                onClick={() => onClearAddress('company')}
              >
                <span className="icon-delete"></span>{' '}
              </button>
            )}
          </div>

          <div className="form-control">
            <Input
              maxLength="50"
              type="text"
              id="company_name"
              disabled={disabled}
              label={t('common-company-name')}
              error={errors?.company_address?.company_name}
              {...register('company_address.company_name', {
                validate: (value, allValues) =>
                  validateCompanyField('company_name', allValues),
              })}
            />
          </div>

          <Grid container spacing={1}>
            <Grid size={{xs: 12, sm: 8, md: 8}}>
              <div className="form-control">
                <Input
                  maxLength="150"
                  type="text"
                  id="address_line_1_company"
                  disabled={disabled}
                  label={t('common-address-name')}
                  error={errors?.company_address?.address_line_1}
                  {...register('company_address.address_line_1', {
                    validate: (value, allValues) =>
                      validateCompanyField('address_line_1', allValues),
                  })}
                />
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="20"
                  type="text"
                  id="address_number_company"
                  disabled={disabled}
                  label={t('common-address-number')}
                  error={errors?.company_address?.address_number}
                  {...register('company_address.address_number', {
                    validate: (value, allValues) =>
                      validateCompanyField('address_number', allValues),
                  })}
                />
              </div>
            </Grid>
          </Grid>

          <div className="form-control">
            <label htmlFor="address_line_2_company">
              {t('common-address-line-2')}
            </label>
            <input
              maxLength="150"
              type="text"
              id="address_line_2_company"
              disabled={disabled}
              {...register('company_address.address_line_2')}
            />
          </div>

          <Grid container spacing={3}>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <label htmlFor="country_company">{t('common-country')}</label>
                <select
                  name="area_code_company"
                  id="country_company"
                  className="normal"
                  disabled={disabled}
                  error={errors?.company_address?.area_code}
                  {...register('company_address.area_code', {
                    validate: (value, allValues) =>
                      validateCompanyField('area_code', allValues),
                  })}
                >
                  <option key="" value="">
                    {t('common-select-country')}
                  </option>
                  {countries.map((country) => (
                    <option key={country.area_code} value={country.area_code}>
                      {t('countries-' + country.area_code)}
                    </option>
                  ))}
                </select>
                {errors?.company_address?.area_code && (
                  <span className="input__error">
                    {errors.company_address.area_code.message}
                  </span>
                )}
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="50"
                  type="text"
                  id="city_company"
                  disabled={disabled}
                  label={t('common-city')}
                  error={errors?.company_address?.city}
                  {...register('company_address.city', {
                    validate: (value, allValues) =>
                      validateCompanyField('city', allValues),
                  })}
                />
              </div>
            </Grid>
            <Grid size={{xs: 12, sm: 4, md: 4}}>
              <div className="form-control">
                <Input
                  maxLength="50"
                  type="text"
                  id="postal_code_company"
                  disabled={disabled}
                  label={t('common-zip-cp')}
                  error={errors?.company_address?.postal_code}
                  {...register('company_address.postal_code', {
                    validate: (value, allValues) =>
                      validateCompanyField('postal_code', allValues),
                  })}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export default HacAddressSection;
